// Internet Health Score V1 thresholds. Edit here; the logic in score.ts stays the same.
//
// Assumptions (V1, provisional):
// - The user's plan speed is unknown, so speed is judged on absolute usefulness with diminishing returns:
//   anything from ~300 Mbps down (~100 Mbps up) scores full marks; 500 Mbps is not "healthier" than 300.
// - Responsiveness matters as much as speed: idle latency, jitter, and how much latency rises while the
//   connection is busy (bufferbloat) carry over half the weight.
// - The rise-under-load bands follow common bufferbloat grading (≈ <30 ms excellent, <60 ms good, >200 ms poor).
// - One severe weakness must not be averaged away: caps limit the total when any single metric scores low.

export type MetricKey = 'download' | 'upload' | 'latency' | 'jitter' | 'responsiveness';

/** Piecewise-linear curve: [metric value, sub-score 0–100], sorted by value. Values outside the range are clamped. */
export type Curve = ReadonlyArray<readonly [number, number]>;

export const SCORE_CURVES: Record<MetricKey, Curve> = {
  // Mbps, higher is better.
  download: [[0, 0], [1, 10], [5, 40], [10, 55], [25, 75], [50, 85], [100, 93], [200, 98], [300, 100]],
  upload: [[0, 0], [0.5, 10], [1, 25], [3, 50], [5, 62], [10, 78], [20, 88], [50, 96], [100, 100]],
  // ms, lower is better.
  latency: [[0, 100], [10, 100], [20, 97], [30, 92], [50, 80], [75, 65], [100, 50], [150, 30], [250, 10], [400, 0]],
  jitter: [[0, 100], [2, 100], [5, 92], [10, 80], [20, 60], [30, 45], [50, 25], [100, 0]],
  // Latency increase while downloading (loaded − idle), ms, lower is better.
  responsiveness: [[0, 100], [10, 98], [30, 90], [60, 75], [200, 45], [400, 20], [800, 0]],
};

/** Weights sum to 1. Missing optional metrics are dropped and the remaining weights rescaled. */
export const SCORE_WEIGHTS: Record<MetricKey, number> = {
  download: 0.25,
  upload: 0.15,
  latency: 0.2,
  jitter: 0.15,
  responsiveness: 0.25,
};

/** If any sub-score is below `below`, the total cannot exceed `maxScore`. Checked in order; the lowest cap wins. */
export const SCORE_CAPS = [
  { below: 25, maxScore: 49 },
  { below: 50, maxScore: 69 },
] as const;

export type Rating = 'excellent' | 'good' | 'fair' | 'poor';

export const RATING_BANDS: ReadonlyArray<{ min: number; rating: Rating }> = [
  { min: 85, rating: 'excellent' },
  { min: 70, rating: 'good' },
  { min: 50, rating: 'fair' },
  { min: 0, rating: 'poor' },
];
